/**
 * Shared types, step config, and presentational atoms for OnboardingWizard.
 * Extracted to keep OnboardingWizard.tsx under the 200-line threshold.
 *
 * Layout CSS (page grid, card, rail, footer, eyebrow) lives in the shared
 * DomainSetupWizard.module.css — this file's CSS covers only the Review-step
 * key/value rows which are unique to this wizard.
 */
import reviewStyles from './OnboardingWizard.module.css';

export interface OnboardingStep {
  id: 'activate' | 'domain' | 'waf' | 'cert' | 'ssl' | 'review';
  label: string;
  title: string;
  description: string;
  skippable: boolean;
}

/** Which cert acquisition method the user chose on the cert step. */
export type CertMode = 'letsencrypt' | 'upload';
/** Which ACME CA to use for managed cert issuance. */
export type CertProvider = 'letsencrypt' | 'zerossl';
/** Outcome of the cert step — 'none' means skipped. */
export type CertResult = 'none' | 'letsencrypt' | 'zerossl' | 'upload';

export function certResultLabel(r: CertResult): string {
  if (r === 'letsencrypt') return "Let's Encrypt";
  if (r === 'zerossl') return 'ZeroSSL';
  if (r === 'upload') return 'Uploaded';
  return 'Skipped';
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'activate',
    label: 'Activate',
    title: 'Activate WAF',
    description: 'Provision your WAF service to get started.',
    skippable: false,
  },
  {
    id: 'domain',
    label: 'Domain',
    title: 'Add your domain',
    description: 'Tell us the domain to protect and where to fetch content from.',
    skippable: false,
  },
  {
    id: 'waf',
    label: 'WAF',
    title: 'Turn on WAF',
    description:
      'Protect your domain from web attacks with managed OWASP rules, bot and DDoS protection.',
    skippable: true,
  },
  {
    id: 'cert',
    label: 'Certificate',
    title: 'Edge certificate',
    description: 'Issue or upload an SSL certificate for your domain. You can skip this and add one later.',
    skippable: true,
  },
  {
    id: 'ssl',
    label: 'TLS',
    title: 'Secure the connection',
    description: 'Choose how traffic between the edge and your origin is encrypted.',
    skippable: true,
  },
  {
    id: 'review',
    label: 'Review',
    title: 'Review & finish',
    description: 'Confirm your settings and activate protection.',
    skippable: false,
  },
];

/**
 * Key/value row used exclusively inside the Review step card body.
 * `on` applies the success color token (e.g. for "WAF: On").
 */
export function ReviewRow({ label, value, on }: { label: string; value: string; on?: boolean }) {
  return (
    <div className={reviewStyles.reviewRow}>
      <span className={reviewStyles.reviewKey}>{label}</span>
      <span className={on ? `${reviewStyles.reviewValue} ${reviewStyles.reviewValueOn}` : reviewStyles.reviewValue}>
        {value}
      </span>
    </div>
  );
}

/** Container for the Review step's key/value list. */
export function ReviewGrid({ children }: { children: React.ReactNode }) {
  return <div className={reviewStyles.reviewGrid}>{children}</div>;
}
